import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Collapse from '@material-ui/core/Collapse';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';

import { isoDateTimeToLabel, formatAmountValue } from 'helpers/helpers';
import Tooltip from 'components/Tooltip/Tooltip';
import MaterialTable from 'components/Table/Table';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import { AdvisorsStore, Account, Advisor } from 'stores/advisorsStore';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';

import useStyles, { rightCellStyles } from 'pages/Accounts/AccountsList/styles/Accounts'; //centerCellStyles,
import { AccountStatus, Currency } from 'enums';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  store: AdvisorsStore;
  setAdvisor: (advisor: { realm: string; name: string }) => void;
  setEditDialog: (show: boolean) => void;
  setConfirmDialog: (show: boolean) => void;
  setFinishedDialog: (show: boolean) => void;
  setConfirmAction: (action: 'ACTIVATE' | 'REMOVE') => void;
  data: Advisor;
}

const fetchRowDetail = async (
  store: AdvisorsStore,
  open: boolean,
  setOpen: (open: boolean) => void,
  accounts: null | Account[],
  realm: string,
  username: string,
  setAccounts: (accounts: null | Account[]) => void,
  setKeycloakId: (id: string) => void,
  setAum: (aum: number) => void,
) => {
  setOpen(!open);
  if (!accounts && !open) {
    const response = await store.fetchAccounts(realm, username);
    setAccounts(response);
    const totalAum = response ? response.reduce((sum, c) => sum + (c.aum || 0), 0) : 0;
    setAum(totalAum);
    response?.length && setKeycloakId(response[0].advisorKeycloakId);
  }
};

const editAccountsList = (id: string, accountsList: string[], setAccountsList: (accounts: any) => void) => {
  const index = accountsList.indexOf(id);
  if (index === -1) {
    setAccountsList((list: string[]) => [...list, id]);
  } else {
    setAccountsList(accountsList.filter((i) => i !== id));
  }
};

const AdvisorRow: React.FC<Props> = (props) => {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<null | Account[]>(null);
  const [aum, setAum] = useState(0);
  const [accountsList, setAccountsList] = useState<string[]>([]);
  const [transferDialog, setTransferDialog] = useState(false);
  const [foundAdvisors, setFoundAdvisors] = useState<Advisor[]>([]);
  const [transferRealm, setTransferRealm] = useState('');
  const [transferName, setTransferName] = useState('');
  const [keycloakId, setKeycloakId] = useState<undefined | string>(undefined);
  const [impOpen, setImpOpen] = useState(false);

  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <>
      <TableRow
        className={classes.advisorRow}
        style={{ borderTop: open ? 'solid #666 1px' : undefined }}
        onClick={() => {
          fetchRowDetail(
            props.store,
            open,
            setOpen,
            accounts,
            props.data.intermediaryCode,
            props.data.username,
            setAccounts,
            setKeycloakId,
            setAum,
          );
        }}
      >
        <TableCell style={{ width: '40px' }}>
          <IconButton aria-label="expand row" size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{props.data.code}</TableCell>
        <TableCell>{props.data.username}</TableCell>
        <TableCell>{props.data.name}</TableCell>
        <TableCell>{t(`intermediary_${props.data.intermediaryCode}`)}</TableCell>
        <TableCell>
          <Tooltip title={t(`role_${props.data.role}_info`)} placement="top">
            <span>{t(`role_${props.data.role}`)}</span>
          </Tooltip>
        </TableCell>
        <TableCell>{props.data.activeSince ? isoDateTimeToLabel(props.data.activeSince, 'dd.MM.yyyy') : '—'}</TableCell>
        <TableCell>
          <Button
            onClick={(event) => {
              props.store.impersonate(props.data.realm, keycloakId);
              setImpOpen(true);
              event.stopPropagation();
            }}
            disabled={!keycloakId}
            variant="contained"
            color="primary"
          >
            {t('backOffice_advisors_impersonate')}
          </Button>
        </TableCell>
        <TableCell>
          <Tooltip title={t('backOffice_advisors_edit')} placement="top">
            <span>
              <IconButton
                size="medium"
                className="accountDetail-button"
                onClick={(event) => {
                  props.setAdvisor({ realm: props.data.realm, name: props.data.username });
                  props.setEditDialog(true);
                  event.stopPropagation();
                }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t('backOffice_advisors_remove')} placement="top">
            <span>
              <IconButton
                size="medium"
                className="accountDetail-button"
                onClick={(event) => {
                  props.setConfirmAction('REMOVE');
                  props.setAdvisor({ realm: props.data.realm, name: props.data.username });
                  props.setConfirmDialog(true);
                  event.stopPropagation();
                }}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={open ? classes.accountsOpen : classes.accountsCollapsed} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit style={{ margin: '24px' }}>
            {accounts ? (
              <>
                {accounts.length > 0 && (
                  <>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="h3">
                          {t('backOffice_advisors_accounts')} <strong>{props.data.name}</strong> ({t('general_count')}:{' '}
                          <strong>{accounts.length}</strong>, {t('general_aum')}:{' '}
                          <strong>{formatAmountValue(aum, Currency.CZK)}</strong>)
                        </Typography>
                        <div style={{ background: '#f6f6f6' }}>
                          <MaterialTable
                            data={accounts}
                            columns={[
                              {
                                title: t('backOffice_advisors_accountId'),
                                field: 'accountId',
                              },
                              {
                                title: t('backOffice_advisors_clientName'),
                                field: 'clientName',
                                render: ({ clientName }) => (clientName !== 'null null' ? clientName : ''),
                              },
                              {
                                title: t('general_accountName'),
                                field: 'title',
                              },
                              {
                                title: t('backOffice_advisors_variableSymbol'),
                                field: 'variableSymbol',
                                render: ({ variableSymbol }) => variableSymbol || '—',
                              },
                              {
                                title: t('backOffice_advisors_created'),
                                field: 'created',
                                render: ({ created }) => <>{isoDateTimeToLabel(created, 'dd.MM.yyyy')}</>,
                              },
                              {
                                title: t('general_aum'),
                                field: 'aum',
                                render: ({ aum, currency }) =>
                                  aum !== undefined && aum !== null ? formatAmountValue(aum, currency) : '—',
                                headerStyle: { textAlign: 'right' },
                                cellStyle: { ...rightCellStyles },
                              },
                              {
                                title: t('backOffice_advisors_status'),
                                field: 'status',
                                render: ({ status }) => t(`options_accountStatus_${status}`),
                              },
                              {
                                title: t('backOffice_advisors_pendingSignature'),
                                field: 'pendingSignature',
                                render: ({ pendingSignature }) => (pendingSignature ? t('general_yes') : ''),
                              },
                              {
                                title: '',
                                field: 'impersonation',
                                render: ({ advisorKeycloakId, accountId, status }) => (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!impOpen}
                                    onClick={async () =>
                                      await props.store.goToAccount(props.data.realm, accountId, status)
                                    }
                                  >
                                    {t('backOffice_advisors_goToAccount')}
                                  </Button>
                                ),
                              },
                              {
                                title: t('backOffice_advisors_transfer'),
                                field: 'transfer',
                                render: ({ accountId, status }) => (
                                  <>
                                    <Checkbox
                                      checked={accountsList.indexOf(accountId) > -1}
                                      onChange={() => {
                                        editAccountsList(accountId, accountsList, setAccountsList);
                                      }}
                                      disabled={
                                        ![AccountStatus.AWAITING_APPROVAL, AccountStatus.ACTIVE].includes(status)
                                      }
                                    />
                                  </>
                                ),
                              },
                            ]}
                            options={{
                              sorting: true,
                              draggable: false,
                              search: false,
                              toolbar: false,
                              emptyRowsWhenPaging: false,
                              pageSize: 10,
                            }}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} style={{ textAlign: 'right' }}>
                        <Button
                          variant="contained"
                          color="default"
                          disabled={accountsList.length === 0}
                          onClick={() => {
                            setAccountsList([]);
                          }}
                          style={{ marginRight: '8px' }}
                        >
                          {t('general_cancelSelection')}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={accountsList.length === 0}
                          onClick={() => {
                            setTransferDialog(true);
                          }}
                        >
                          {t('backOffice_advisors_transfer')}
                        </Button>
                      </Grid>
                    </Grid>

                    <Dialog open={transferDialog} maxWidth="sm">
                      <DialogHeader onClose={() => setTransferDialog(false)}>
                        {t('backOffice_advisors_transfer')}
                      </DialogHeader>
                      <DialogContent>
                        <Formik
                          initialValues={{
                            advisorUsername: transferName,
                            advisorRealm: transferRealm,
                            foundAdvisor: ``,
                          }}
                          onSubmit={() => {}}
                          enableReinitialize
                        >
                          {(formikProps) => {
                            return (
                              <FormWithoutEnterSubmit>
                                <Grid container>
                                  <Grid item xs={12}>
                                    <FormTextField
                                      fast={false}
                                      name="advisorUsername"
                                      label={t('general_username')}
                                      afterChange={() => {
                                        const chars = formikProps.values.advisorUsername.length;
                                        if (chars < 3) {
                                          setFoundAdvisors([]);
                                          formikProps.values.foundAdvisor = ``;
                                        }
                                        if (props.store.advisors && chars > 2) {
                                          const found = props.store.advisors.filter(
                                            (a: Advisor) =>
                                              a.username.substr(0, chars) ===
                                              formikProps.values.advisorUsername.substr(0, chars),
                                          );
                                          setFoundAdvisors(found);
                                          if (found.length === 1) {
                                            setTransferName(found[0].username);
                                            setTransferRealm(found[0].realm);
                                          }
                                        }
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    {formikProps.values.advisorUsername?.length < 3 ? (
                                      t('backOffice_advisors_moreChars')
                                    ) : (
                                      <>
                                        {t('backOffice_advisors_foundAdvisors')} ({foundAdvisors.length})
                                      </>
                                    )}
                                    <FormSelectField
                                      fast={false}
                                      name={`foundAdvisor`}
                                      options={foundAdvisors.map((advisor: Advisor) => ({
                                        value: `{"realm":"${advisor.realm}","username":"${advisor.username}"}`,
                                        label: `${advisor.username}: ${advisor.name} (${t(
                                          `intermediary_${advisor.intermediaryCode}`,
                                        )})`,
                                      }))}
                                      afterChange={() => {
                                        formikProps.values.advisorUsername = JSON.parse(
                                          formikProps.values.foundAdvisor,
                                        ).username;
                                        props.store.advisors &&
                                          setFoundAdvisors([
                                            ...props.store.advisors.filter(
                                              (a: Advisor) => a.username === formikProps.values.advisorUsername,
                                            ),
                                          ]);
                                        setTransferRealm(JSON.parse(formikProps.values.foundAdvisor).realm);
                                        setTransferName(JSON.parse(formikProps.values.foundAdvisor).username);
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </FormWithoutEnterSubmit>
                            );
                          }}
                        </Formik>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          variant="contained"
                          color="default"
                          onClick={() => {
                            setTransferDialog(false);
                          }}
                        >
                          {t('general_cancel')}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={foundAdvisors.length !== 1}
                          onClick={async () => {
                            setTransferDialog(false);
                            const ok = await props.store.transfer(
                              props.data.intermediaryCode,
                              props.data.username,
                              transferRealm,
                              transferName,
                              accountsList,
                            );
                            ok && props.setFinishedDialog(true);
                          }}
                        >
                          {t('backOffice_advisors_transfer')}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
                {accounts.length === 0 && (
                  <Typography variant="h3">
                    {t('backOffice_advisors_noAccountsUser')} <strong>{props.data.name}</strong>{' '}
                    {t('backOffice_advisors_noAccountsInfo')}
                  </Typography>
                )}
              </>
            ) : (
              <div className={classes.loadingLine}>
                <LoadingIndicator size={56} />
              </div>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default observer(AdvisorRow);
